import { reactive, toRefs, computed } from 'vue'
import { encryptAES, decryptAES } from '@/utils/crypto'
import { ClientType } from '@/constants/client'
import { defineStore } from '../store'
import { localData, sessionData } from '../storage'
import plus from '@/utils/h5plus'
import cryptojs from 'crypto-js'

// 初始化登录信息
function initLoginInfo(): Model.LoginRsp {
    return {
        expiresAt: 0, // 过期时间
        loginId: 0, // 登录ID
        token: '', // 新服务Token
        userId: 0, // 用户ID
    }
}

/**
 * 登录存储对象
 */
export const useLoginStore = defineStore(() => {
    const localLoginInfo = localData.getValue('loginInfo')
    const sessionLoginInfo = sessionData.getValue('loginInfo')

    const state = reactive({
        logining: false,
        loginInfo: localLoginInfo || sessionLoginInfo || initLoginInfo()
    })

    // 登录令牌
    const token = computed(() => state.loginInfo.token)

    // 用户ID
    const userId = computed(() => state.loginInfo.userId)

    // 登录ID
    const loginId = computed(() => state.loginInfo.loginId)

    // 获取终端类型
    const getClientType = () => {
        const os = plus.getSystemInfo('os')
        switch (os) {
            case 'Android': {
                return ClientType.Android
            }
            case 'iOS': {
                return ClientType.IOS
            }
            default: {
                return ClientType.Web
            }
        }
    }

    // 更新登录信息
    const setLoginInfo = (data: Model.LoginRsp, persist = false) => {
        state.loginInfo = data
        if (persist) {
            localData.setValue('loginInfo', data) // 缓存登录状态到本地
            sessionData.reset('loginInfo')
        } else {
            sessionData.setValue('loginInfo', data) // 缓存登录状态到会话
            localData.reset('loginInfo')
        }
    }

    // 获取自动登录信息
    const getAutoLoginData = () => {
        try {
            const encryptedData = localData.getValue('autoLoginEncryptedData')
            const decryptedString = decryptAES(encryptedData)
            return JSON.parse(decryptedString) as Model.LoginReq
        } catch (err) {
            clearAutoLoginData()
            return undefined
        }
    }

    // 更新自动登录信息
    const setAutoLoginData = (data: Model.LoginReq) => {
        const encryptedString = encryptAES(JSON.stringify(data)) // 数据加密
        localData.setValue('autoLoginEncryptedData', encryptedString)
    }

    // 校验登录密码
    const checkLoginPassword = (password: string) => {
        const loginData = getAutoLoginData()
        if (loginData) {
            const encryptedPassword = cryptojs.SHA256(loginId.value + password).toString()
            return loginData.password === encryptedPassword
        }
        return false
    }

    // 清除自动登录信息
    const clearAutoLoginData = () => {
        localData.reset('autoLoginEncryptedData')
    }

    // 清除登录信息
    const clearLoginInfo = () => {
        localData.reset('loginInfo')
        sessionData.reset('loginInfo', 'userRoutes', 'performanceStepTypes', 'performanceTemplates')
        state.loginInfo = initLoginInfo()
    }

    return {
        ...toRefs(state),
        token,
        userId,
        loginId,
        getClientType,
        setLoginInfo,
        getAutoLoginData,
        setAutoLoginData,
        checkLoginPassword,
        clearAutoLoginData,
        clearLoginInfo,
    }
})